import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { AuthButton } from '@/features/auth/components/auth-button';
import { AuthForm } from '@/features/auth/components/auth-form';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { AuthLink } from '@/features/auth/components/auth-link';

const loginFormSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginFormSchema>;

function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);

  const { auth } = Route.useRouteContext();
  const { redirectTo } = Route.useSearch();
  const { signIn } = auth;

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // Clear any previous errors
    form.clearErrors('root');

    try {
      const result = await signIn({
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        // TODO: We should match codes to our own error messages?
        form.setError('root', {
          type: 'manual',
          message: result.error.message || 'Failed to sign in',
        });
        return;
      }

      window.location.href = redirectTo;
    } catch {
      form.setError('root', {
        type: 'manual',
        message: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthForm
        onSubmit={form.handleSubmit(handleSubmit)}
        title="Sign in to your account"
      >
        <Form {...form}>
          <div className="grid grid-rows-[auto_1fr] gap-4">
            {/* Error message area - only takes space when needed */}
            <div className="overflow-hidden">
              <div
                className={`transition-all duration-200 ${
                  form.formState.errors.root
                    ? 'max-h-20 opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {form.formState.errors.root?.message}
                  </div>
                </div>
              </div>
            </div>

            {/* Form fields area */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="name@example.com"
                        autoCapitalize="none"
                        autoComplete="email"
                        autoCorrect="off"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder="Password"
                        autoComplete="current-password"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Form>

        <AuthButton type="submit" disabled={isLoading}>
          {isLoading ? 'Signing in...' : 'Login'}
        </AuthButton>
      </AuthForm>
      <AuthLink
        to="/register"
        text="Don't have an account?"
        linkText="Sign up"
      />
      <AuthLink
        to="/forgot-password"
        text=""
        linkText="Forgot your password?"
      />
    </AuthLayout>
  );
}

const loginSearchSchema = z.object({
  redirectTo: z.string().default('/').catch('/'),
});

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirectTo });
    }
  },
  component: LoginPage,
});
